import React from "react";

import {
  WatchBoolean,
  WatchNumber,
  WatchText,
  WatchVec2,
} from "@piqnt/runtime-core/react/WatchValue";
import {
  Pair,
  PairKey,
  PairValue,
  Subtree,
  SubtreeBody,
  SubtreeHeader,
} from "@piqnt/runtime-core/react/Subtree";

import { useMiddleware } from "@piqnt/runtime-core/react/ReactMount";
import { getLabel } from "@piqnt/runtime-core/common/Label";
import {
  getKey,
  PlainBody,
  PlainFixture,
} from "@piqnt/runtime-space/drawing-model";

import { LiveBody } from "./LiveBody";
import { SpaceContext } from "../space";
import { FixtureCreate } from "./FixtureCreate";
import { FixtureView } from "./FixtureView";

interface PropsWithBody {
  body: PlainBody;
  fixture?: PlainFixture;
}

export const BodyView: React.FC<PropsWithBody> = ({ body }) => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const editable = context.editable.value;

  if (!body) return null;

  return (
    <Subtree open>
      <SubtreeHeader>
        <LiveBody body={body} />
      </SubtreeHeader>
      <SubtreeBody>
        <Pair>
          <PairKey>label</PairKey>
          <PairValue>
            <WatchText
              editable={editable}
              getValue={() => getLabel(body)}
              setValue={(v) =>
                emit("body-set-props", {
                  key: getKey(body),
                  props: { lookupId: v },
                })
              }
            />
          </PairValue>
        </Pair>
        <Pair>
          <PairKey>type</PairKey>
          <PairValue>
            <WatchText
              editable={editable}
              getValue={() => body.type}
              setValue={(v) => {
                if (v !== "dynamic" && v !== "kinematic" && v !== "static")
                  return;
                emit("body-set-props", {
                  body: getKey(body),
                  props: { type: v },
                });
              }}
            />
          </PairValue>
        </Pair>
        <Pair>
          <PairKey>bullet</PairKey>
          <PairValue>
            <WatchBoolean
              editable={editable}
              getValue={() => body.bullet}
              setValue={(v) =>
                emit("body-set-props", {
                  body: getKey(body),
                  props: { bullet: v },
                })
              }
            />
          </PairValue>
        </Pair>
        <Pair>
          <PairKey>position</PairKey>
          <PairValue>
            <WatchVec2
              editable={editable}
              getValue={() => body.position}
              setValue={(v) =>
                emit("body-set-props", {
                  body: getKey(body),
                  props: { position: v },
                })
              }
            />
          </PairValue>
        </Pair>
        <Pair>
          <PairKey>angle</PairKey>
          <PairValue>
            <WatchNumber
              editable={editable}
              getValue={() => body.angle}
              setValue={(v) =>
                emit("body-set-props", {
                  body: getKey(body),
                  props: { angle: v },
                })
              }
            />
          </PairValue>
        </Pair>
        <Pair>
          <PairKey>linearVelocity</PairKey>
          <PairValue>
            <WatchVec2
              editable={editable}
              getValue={() => body.linearVelocity}
              setValue={(v) =>
                emit("body-set-props", {
                  body: getKey(body),
                  props: { linearVelocity: v },
                })
              }
            />
          </PairValue>
        </Pair>
        <Pair>
          <PairKey>angularVelocity</PairKey>
          <PairValue>
            <WatchNumber
              editable={editable}
              getValue={() => body.angularVelocity}
              setValue={(v) =>
                emit("body-set-props", {
                  body: getKey(body),
                  props: { angularVelocity: v },
                })
              }
            />
          </PairValue>
        </Pair>
        {body.fixtures.map((fix) => (
          <FixtureView fixture={fix} key={fix.__key} />
        ))}
        <FixtureCreate body={body} />
      </SubtreeBody>
    </Subtree>
  );
};
